import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lightbulb, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  ApiError,
  createBrightIdea,
  listBrightIdeas,
  respondBrightIdea,
  type BrightIdea,
  type IdeaReflectionResponse,
} from "@/lib/api";
import { entryWhen } from "@/lib/format";

// Ideas old enough to look at with fresh eyes (the design's revisit card).
const REVISIT_AFTER_DAYS = 14;

const RESPONSE_LABEL: Record<IdeaReflectionResponse, string> = {
  still_feels_right: "Still feels right",
  funny_in_hindsight: "Funny in hindsight",
  worth_revisiting: "Worth revisiting",
};

function daysAgo(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}
function agoLabel(iso: string): string {
  const d = daysAgo(iso);
  if (d < 14) return `${d} days ago`;
  const w = Math.round(d / 7);
  return `${w} week${w === 1 ? "" : "s"} ago`;
}

export default function BrightIdeas() {
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState<BrightIdea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [responding, setResponding] = useState(false);

  async function load() {
    try {
      setIdeas(await listBrightIdeas());
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) navigate("/signin");
      else setError("Couldn't load your ideas.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function save() {
    if (!draft.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await createBrightIdea(draft.trim());
      setDraft("");
      // The AI tags land asynchronously; a short delayed refresh picks them up.
      await load();
      setTimeout(() => void load(), 3000);
    } catch {
      setError("Couldn't save your idea. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function respond(id: string, response: IdeaReflectionResponse) {
    setResponding(true);
    try {
      await respondBrightIdea(id, response);
      await load();
    } catch {
      setError("Couldn't save that. Please try again.");
    } finally {
      setResponding(false);
    }
  }

  // Oldest unreflected idea past the fresh-eyes window.
  const revisit = [...ideas]
    .reverse()
    .find((i) => !i.reflection_response_type && daysAgo(i.created_at) >= REVISIT_AFTER_DAYS);

  return (
    <div className="pb-16">
      <div className="flex items-center gap-2 px-4 pb-2 pt-6">
        <Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate("/app")}>
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
        <h1 className="flex-1 text-lg font-semibold text-foreground">Bright Ideas</h1>
      </div>

      <div className="flex flex-col gap-3 px-6 pt-2">
        {error && <p className="text-sm text-status-red-text">{error}</p>}

        {/* Capture */}
        <Card>
          <CardContent className="flex flex-col gap-2.5 px-4 py-3.5">
            <Textarea
              placeholder="What's the big idea?"
              rows={3}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <Button fullWidth disabled={saving || !draft.trim()} onClick={save}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
              ) : (
                <>
                  <Lightbulb className="h-4 w-4" strokeWidth={2} />
                  Save Idea
                </>
              )}
            </Button>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Big plans, sudden decisions, exciting thoughts — drop them here to revisit
              with fresh eyes later.
            </p>
          </CardContent>
        </Card>

        {/* Fresh-eyes revisit */}
        {revisit && (
          <Card className="bg-secondary">
            <CardContent className="flex flex-col gap-2.5 px-4 py-3.5">
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                From {agoLabel(revisit.created_at)}
              </p>
              <p className="text-[15px] font-medium text-foreground">
                &ldquo;{revisit.content}&rdquo;
              </p>
              <p className="text-[13px] text-muted-foreground">
                Still feel the same way? No pressure either way.
              </p>
              <div className="flex flex-col gap-1.5">
                {(Object.keys(RESPONSE_LABEL) as IdeaReflectionResponse[]).map((r) => (
                  <button
                    key={r}
                    type="button"
                    disabled={responding}
                    onClick={() => respond(revisit.id, r)}
                    className="rounded-md border border-border bg-card px-3 py-2 text-left text-sm text-foreground transition-colors hover:border-primary/50"
                  >
                    {RESPONSE_LABEL[r]}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Saved ideas */}
        <p className="mt-1 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
          Saved Ideas
        </p>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
            <span className="text-sm">Loading…</span>
          </div>
        ) : ideas.length === 0 ? (
          <Card>
            <CardContent className="px-4 py-6 text-center">
              <p className="text-sm font-medium text-foreground">Nothing in the box yet</p>
              <p className="mt-1 text-[13px] text-muted-foreground">
                Ideas you save here — or during a check-in — will collect for a
                second look.
              </p>
            </CardContent>
          </Card>
        ) : (
          ideas.map((it) => (
            <Card key={it.id}>
              <CardContent className="px-4 py-3">
                <p className="text-sm leading-normal text-foreground">{it.content}</p>
                {it.ai_analysis && (
                  <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                    {it.ai_analysis}
                  </p>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {it.tags.map((t) => (
                    <Badge key={t} variant="outline">
                      {t}
                    </Badge>
                  ))}
                  <span className="text-xs text-muted-foreground">
                    {entryWhen(it.created_at)}
                  </span>
                </div>
                {it.reflection_response_type && (
                  <p className="mt-2 border-t border-border pt-2 text-xs text-muted-foreground">
                    With fresh eyes: {RESPONSE_LABEL[it.reflection_response_type].toLowerCase()}
                    {it.reflection_response_note ? ` — ${it.reflection_response_note}` : ""}
                  </p>
                )}
              </CardContent>
            </Card>
          ))
        )}
        <p className="text-center text-xs text-muted-foreground">
          Observations, not advice. An idea can just sit here — that's the point.
        </p>
      </div>
    </div>
  );
}
